/**
 * Drop-in component that renders a "View on Discord" button for an event when
 * the event has been posted to one or more Discord channels (i.e. there's at
 * least one row in event_discord_posts with a thread_id).
 *
 * Usage:
 *   <EventDiscordLinks eventId={event.id} />
 *   <EventDiscordLinks eventId={event.id} compact />
 *
 * Renders nothing if:
 *   - the event has no discord posts with a thread_id, OR
 *   - REACT_APP_DISCORD_GUILD_ID is not set in the build environment
 *
 * Setup: add REACT_APP_DISCORD_GUILD_ID to your Vercel environment with the
 * same value as DISCORD_GUILD_ID, then redeploy.
 */
import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

// ─── Module-level cache ──────────────────────────────────────────────────────
// Every event card / page that uses this component shares one fetch, kept in
// memory for the session. The cache is invalidated only on full page reload.
let cachedMap = null;
let pendingFetch = null;
const subscribers = new Set();

async function fetchAll() {
  if (cachedMap) return cachedMap;
  if (pendingFetch) return pendingFetch;
  pendingFetch = (async () => {
    const { data } = await supabase
      .from("event_discord_posts")
      .select("event_id, channel_id, thread_id, posted_at")
      .order("posted_at", { ascending: false });
    const map = {};
    (data || []).forEach(r => {
      if (!r.thread_id) return; // can't link without a thread
      if (!map[r.event_id]) map[r.event_id] = [];
      map[r.event_id].push({
        channel_id: r.channel_id,
        thread_id:  r.thread_id,
        posted_at:  r.posted_at,
      });
    });
    cachedMap = map;
    pendingFetch = null;
    subscribers.forEach(fn => fn(map));
    return map;
  })();
  return pendingFetch;
}

export function useEventDiscordPosts() {
  const [map, setMap] = useState(cachedMap || {});
  useEffect(() => {
    let cancelled = false;
    fetchAll().then(m => { if (!cancelled) setMap(m); });
    subscribers.add(setMap);
    return () => { cancelled = true; subscribers.delete(setMap); };
  }, []);
  return map;
}

// Build a discord.com thread URL. Returns null if either guild_id or thread_id is missing.
export function discordThreadUrl(threadId) {
  const guildId = process.env.REACT_APP_DISCORD_GUILD_ID;
  if (!guildId || !threadId) return null;
  return `https://discord.com/channels/${guildId}/${threadId}`;
}

// ─── Component ───────────────────────────────────────────────────────────────
export function EventDiscordLinks({ eventId, compact = false }) {
  const map = useEventDiscordPosts();
  const threads = map[eventId] || [];
  // De-dupe by thread_id in case the same event was posted twice to the same place
  const seen = new Set();
  const links = [];
  for (const t of threads) {
    if (seen.has(t.thread_id)) continue;
    const url = discordThreadUrl(t.thread_id);
    if (!url) continue;
    seen.add(t.thread_id);
    links.push(url);
  }
  if (links.length === 0) return null;

  // Compact mode: small inline button (fits next to other action buttons in a row)
  // Non-compact: full-width chip-style block (fits in a sidebar / detail area)
  const baseStyle = compact ? {
    fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
    textTransform: "uppercase", padding: "6px 12px",
    background: "rgba(88,101,242,0.10)", border: "1px solid rgba(88,101,242,0.5)",
    color: "#5865f2", textDecoration: "none", cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 5, transition: "all 0.2s",
  } : {
    fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
    color: "#5865f2", background: "rgba(88,101,242,0.10)",
    border: "1px solid rgba(88,101,242,0.45)",
    padding: "5px 12px", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 6,
  };

  // If only one thread, render a single button — most common case, cleanest UX.
  if (links.length === 1) {
    return (
      <a href={links[0]} target="_blank" rel="noopener noreferrer" style={baseStyle}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(88,101,242,0.20)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(88,101,242,0.10)"; }}
      >
        <span style={{ fontSize: compact ? 9 : 11 }}>↗</span>
        {compact ? "Discord" : "View thread on Discord"}
      </a>
    );
  }

  // Multiple threads (event was shared to multiple channels) — stacked chips
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {links.map((url, i) => (
        <a key={url} href={url} target="_blank" rel="noopener noreferrer" style={baseStyle}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(88,101,242,0.20)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(88,101,242,0.10)"; }}
        >
          <span style={{ fontSize: compact ? 9 : 11 }}>↗</span>
          {compact ? `Discord ${i + 1}` : `Thread ${i + 1}`}
        </a>
      ))}
    </div>
  );
}
